import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Activity,
  Plus,
  TrendingUp,
  TrendingDown,
  Clock,
  Target,
  Gauge,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Loader2,
  Factory,
  Calendar,
  Pencil,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { ExportButtons } from '../components/ExportButtons';

import { API_BASE } from '../utils/apiClient';
import { calculerTRS } from '../utils/trsCalcul';

const API = API_BASE;

const Performance = () => {
  const { getAuthHeaders, isManager, isDemoReadonly, user } = useAuth();
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedSupervisor, setSelectedSupervisor] = useState('all');
  const [supervisors, setSupervisors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // saisie en cours de correction
  const formulaireVide = () => ({
    site_id: '',
    date: new Date().toISOString().split('T')[0],
    shift: 'Jour',
    planned_time_minutes: 480,
    downtime_minutes: 0,
    actual_production: 0,
    theoretical_production: 0,
    rejects: 0,
    downtime_reasons: '',
    motif: ''
  });
  const [formData, setFormData] = useState(formulaireVide);

  useEffect(() => {
    fetchData();
  }, [selectedSite]);

  const fetchData = async () => {
    try {
      const daysParam = isDemoReadonly() ? 365 : 30;
      const siteParam = selectedSite !== 'all' ? `&site_id=${selectedSite}` : '';
      const [statsRes, entriesRes, sitesRes] = await Promise.all([
        axios.get(`${API}/trs-stats?days=${daysParam}${siteParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/trs-entries?days=${daysParam}${siteParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() })
      ]);
      axios.get(`${API}/zones`, { headers: getAuthHeaders() })
        .then((r) => setZones((r.data || []).filter((z) => z.type === 'Machine' || z.type === 'Production')))
        .catch(() => setZones([]));
      setStats(statsRes.data);
      setEntries(entriesRes.data);
      setSites(sitesRes.data);
      
      // Extract unique supervisors from entries
      const uniqueSupervisors = [...new Set(entriesRes.data
        .filter(e => e.supervisor_name)
        .map(e => e.supervisor_name)
      )].sort();
      setSupervisors(uniqueSupervisors);
    } catch (error) {
      console.error('Error fetching TRS data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Valeurs du formulaire converties au format du serveur
  const valeursServeur = () => {
    const planifie = parseFloat(formData.planned_time_minutes) || 0;
    const arret = parseFloat(formData.downtime_minutes) || 0;
    const reelle = parseFloat(formData.actual_production) || 0;
    const theorique = parseFloat(formData.theoretical_production) || 0;
    const rejets = parseFloat(formData.rejects) || 0;
    const marche = planifie - arret;
    return {
      planned_time: planifie,
      downtime: arret,
      total_production: reelle,
      conforming_production: reelle - rejets,
      // cadence telle que production théorique = temps de marche × cadence
      theoretical_cadence: marche > 0 ? theorique / (marche / 60) : 0
    };
  };
  const apercu = calculerTRS(valeursServeur());
  // le message ne s'affiche qu'une fois des quantités saisies (formulaire vierge = neutre)
  const erreurVisible = apercu.erreur && (parseFloat(formData.actual_production) > 0 || parseFloat(formData.theoretical_production) > 0) ? apercu.erreur : null;

  // Droit de corriger : l'auteur de la saisie, un manager ou un admin
  const peutModifier = (entry) =>
    !isDemoReadonly() && (isManager() || (user?.role !== 'lecteur' && entry.supervisor_id === user?.id));

  const ouvrirSaisie = () => {
    setEditingId(null);
    setFormData(formulaireVide());
    setDialogOpen(true);
  };

  const ouvrirCorrection = (entry) => {
    const marche = (entry.planned_time || 0) - (entry.downtime || 0);
    setEditingId(entry.id);
    setFormData({
      site_id: entry.zone_id,
      date: entry.date,
      shift: entry.shift || 'Jour',
      planned_time_minutes: entry.planned_time,
      downtime_minutes: entry.downtime,
      actual_production: entry.total_production,
      theoretical_production: Math.round((entry.theoretical_cadence || 0) * marche / 60 * 100) / 100,
      rejects: Math.round(((entry.total_production || 0) - (entry.conforming_production || 0)) * 100) / 100,
      downtime_reasons: '',
      motif: ''
    });
    setDialogOpen(true);
  };

  const supprimerSaisie = async (entry) => {
    if (!window.confirm(`Supprimer la saisie TRS du ${new Date(entry.date).toLocaleDateString('fr-FR')} (${entry.zone_name || ''}) ?`)) return;
    try {
      await axios.delete(`${API}/trs-entries/${entry.id}`, { headers: getAuthHeaders() });
      toast.success('Saisie TRS supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleSubmit = async () => {
    if (!formData.site_id) {
      toast.error('Veuillez sélectionner une machine');
      return;
    }
    if (apercu.erreur) {
      toast.error(apercu.erreur);
      return;
    }
    const corps = { zone_id: formData.site_id, date: formData.date, shift: formData.shift, ...valeursServeur() };
    try {
      if (editingId) {
        await axios.put(`${API}/trs-entries/${editingId}`, { ...corps, motif: formData.motif }, { headers: getAuthHeaders() });
        toast.success('Saisie TRS corrigée');
      } else {
        await axios.post(`${API}/trs-entries`, corps, { headers: getAuthHeaders() });
        toast.success('Données TRS enregistrées');
      }
      setDialogOpen(false);
      setEditingId(null);
      setFormData(formulaireVide());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const getOEEColor = (oee) => {
    if (oee >= 85) return 'text-green-600';
    if (oee >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getOEEBgColor = (oee) => {
    if (oee >= 85) return 'bg-green-500';
    if (oee >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0E346E]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            PERFORMANCE (TRS/OEE)
          </h1>
          <p className="text-slate-500 mt-1">Suivi de l'efficacité globale des équipements</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ExportButtons 
            module="trs" 
            filters={{ 
              zone_id: selectedSite !== 'all' ? selectedSite : undefined 
            }}
            language={i18n.language || 'fr'}
          />
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrer par unité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les unités</SelectItem>
              {sites.filter(s => s.unit_type === 'Ligne' || s.unit_type === 'Machine').map(site => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedSupervisor} onValueChange={setSelectedSupervisor}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrer par superviseur" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les superviseurs</SelectItem>
              {supervisors.map(sup => (
                <SelectItem key={sup} value={sup}>{sup}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isManager() && !isDemoReadonly() && (
            <Button onClick={ouvrirSaisie} className="bg-[#0E346E] hover:bg-[#0A2850]">
              <Plus className="w-4 h-4 mr-2" />
              Saisir TRS
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">TRS Global</p>
                <p className={`text-4xl font-bold mt-1 ${getOEEColor(stats?.avg_oee || 0)}`}>
                  {stats?.avg_oee || 0}%
                </p>
              </div>
              <div className={`w-14 h-14 ${getOEEBgColor(stats?.avg_oee || 0)} rounded-xl flex items-center justify-center shadow-md`}>
                <Gauge className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Disponibilité</p>
                <p className="text-4xl font-bold text-blue-600 mt-1">{stats?.avg_availability || 0}%</p>
              </div>
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <Clock className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Performance</p>
                <p className="text-4xl font-bold text-purple-600 mt-1">{stats?.avg_performance || 0}%</p>
              </div>
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <Activity className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Qualité</p>
                <p className="text-4xl font-bold text-emerald-600 mt-1">{stats?.avg_quality || 0}%</p>
              </div>
              <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              TENDANCE TRS ({isDemoReadonly() ? '12 MOIS' : '7 JOURS'})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.by_day && stats.by_day.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={stats.by_day.filter(d => d.avg_oee !== null)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(v) => v.split('-').slice(1).join('/')} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'TRS']}
                    labelFormatter={(label) => `Date: ${label.split('-').reverse().join('/')}`}
                  />
                  <Line type="monotone" dataKey="avg_oee" stroke="#0E346E" strokeWidth={3} dot={{ fill: '#0E346E' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune donnée disponible</p>
                  <p className="text-sm">Commencez à saisir des données TRS</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Machine - Trend Chart */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <Factory className="w-5 h-5" />
              TENDANCE TRS PAR MACHINE
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.by_machine && stats.by_machine.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.by_machine.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="zone_name" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value}%`, 
                      name === 'avg_oee' ? 'TRS' : name
                    ]} 
                    contentStyle={{ fontSize: '12px' }}
                  />
                  <Bar dataKey="avg_oee" radius={[0, 4, 4, 0]}>
                    {stats.by_machine.map((entry, index) => (
                      <Cell key={index} fill={entry.avg_oee >= 85 ? '#059669' : entry.avg_oee >= 60 ? '#D97706' : '#DC2626'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Factory className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune donnée par machine</p>
                  <p className="text-sm mt-1">Saisissez des données TRS pour voir les tendances</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Entries */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed']">SAISIES RÉCENTES</CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-semibold">Date</th>
                    <th className="text-left py-3 px-2 font-semibold">Unité</th>
                    <th className="text-left py-3 px-2 font-semibold">Superviseur</th>
                    <th className="text-left py-3 px-2 font-semibold">Shift</th>
                    <th className="text-center py-3 px-2 font-semibold">Dispo.</th>
                    <th className="text-center py-3 px-2 font-semibold">Perf.</th>
                    <th className="text-center py-3 px-2 font-semibold">Qualité</th>
                    <th className="text-center py-3 px-2 font-semibold">TRS</th>
                    <th className="py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries
                    .filter(entry => selectedSupervisor === 'all' || entry.supervisor_name === selectedSupervisor)
                    .slice(0, 15)
                    .map(entry => {
                    const site = sites.find(s => s.id === entry.site_id);
                    return (
                      <tr key={entry.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-2">{new Date(entry.date).toLocaleDateString('fr-FR')}</td>
                        <td className="py-3 px-2">{site?.name ? `${site.name} › ` : ''}{entry.zone_name || 'N/A'}</td>
                        <td className="py-3 px-2 text-slate-600">{entry.supervisor_name || '-'}</td>
                        <td className="py-3 px-2">{entry.shift}</td>
                        <td className="py-3 px-2 text-center">{entry.availability}%</td>
                        <td className="py-3 px-2 text-center">{entry.performance}%</td>
                        <td className="py-3 px-2 text-center">{entry.quality}%</td>
                        <td className="py-3 px-2 text-center">
                          <Badge className={`${getOEEBgColor(entry.oee)} text-white`}>
                            {entry.oee}%
                          </Badge>
                          {entry.modifications?.length > 0 && (
                            <span className="block text-[10px] text-slate-400 mt-1" title={`Corrigée par ${entry.modifications[entry.modifications.length - 1].par_nom || '—'}`}>corrigée</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right whitespace-nowrap">
                          {peutModifier(entry) && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => ouvrirCorrection(entry)} title="Corriger cette saisie">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => supprimerSaisie(entry)} title="Supprimer cette saisie" className="text-red-600 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucune saisie TRS</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">{editingId ? 'CORRIGER LA SAISIE TRS' : 'SAISIE TRS'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('performance.unit')} *</Label>
                <Select value={formData.site_id} onValueChange={(v) => setFormData({...formData, site_id: v})}>
                  <SelectTrigger className="border-2">
                    <SelectValue placeholder={t('performance.selectUnit')} />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map(zone => (
                      <SelectItem key={zone.id} value={zone.id}>
                        {(sites.find(s => s.id === zone.site_id)?.name || '') + (sites.find(s => s.id === zone.site_id) ? ' › ' : '') + zone.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('common.date')} *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="border-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Shift</Label>
                <Select value={formData.shift} onValueChange={(v) => setFormData({...formData, shift: v})}>
                  <SelectTrigger className="border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Jour">Jour</SelectItem>
                    <SelectItem value="Soir">Soir</SelectItem>
                    <SelectItem value="Nuit">Nuit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Temps planifié (min)</Label>
                <Input
                  type="number"
                  value={formData.planned_time_minutes}
                  onChange={(e) => setFormData({...formData, planned_time_minutes: e.target.value})}
                  className="border-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Temps d'arrêt (min)</Label>
                <Input
                  type="number"
                  value={formData.downtime_minutes}
                  onChange={(e) => setFormData({...formData, downtime_minutes: e.target.value})}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Production théorique (sur le temps de marche)</Label>
                <Input
                  type="number"
                  value={formData.theoretical_production}
                  onChange={(e) => setFormData({...formData, theoretical_production: e.target.value})}
                  className="border-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Production réelle</Label>
                <Input
                  type="number"
                  value={formData.actual_production}
                  onChange={(e) => setFormData({...formData, actual_production: e.target.value})}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Rejets</Label>
                <Input
                  type="number"
                  value={formData.rejects}
                  onChange={(e) => setFormData({...formData, rejects: e.target.value})}
                  className="border-2"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Causes d'arrêt</Label>
              <Textarea
                value={formData.downtime_reasons}
                onChange={(e) => setFormData({...formData, downtime_reasons: e.target.value})}
                placeholder="Détaillez les causes d'arrêt..."
                className="border-2"
                rows={2}
              />
            </div>
            {editingId && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Motif de la correction</Label>
                <Input
                  value={formData.motif}
                  onChange={(e) => setFormData({...formData, motif: e.target.value})}
                  placeholder="Ex. : erreur de saisie sur la production"
                  className="border-2"
                />
              </div>
            )}

          </div>
          {/* Aperçu calculé en direct : l'erreur bloque l'enregistrement */}
          <div className={`rounded-lg p-3 text-sm ${erreurVisible ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border'}`}>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div><p className="text-xs text-slate-500">Dispo.</p><p className="font-semibold">{apercu.availability}%</p></div>
              <div><p className="text-xs text-slate-500">Perf.</p><p className={`font-semibold ${erreurVisible ? 'text-red-600' : ''}`}>{apercu.performance}%</p></div>
              <div><p className="text-xs text-slate-500">Qualité</p><p className="font-semibold">{apercu.quality}%</p></div>
              <div><p className="text-xs text-slate-500">TRS</p><p className={`font-semibold ${getOEEColor(apercu.oee)}`}>{apercu.oee}%</p></div>
            </div>
            {erreurVisible && (
              <p className="mt-2 text-red-700 flex items-start gap-1">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{erreurVisible}
              </p>
            )}
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSubmit} disabled={!!apercu.erreur} className="bg-[#0E346E] hover:bg-[#0A2850]">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Performance;
